import axios from 'axios';
import { baseURL } from '../Constants';
import { RoundEnd } from '../models/RoundEnd';

const url = `${baseURL}/Analysis`;

export interface AnalysisMatch {
    /** The team id these rows are keyed by — differs from the requested id when a team alias
     *  was resolved, so always decide "us vs them" from this rather than the id you asked with. */
    subjectTeamId?: string;
    gameid: string;
    matchid: string;
    mapName: string;
    season: number;
    division: string;
    team1Id: string;
    team1Name: string;
    team1Logo: string;
    team2Id: string;
    team2Name: string;
    team2Logo: string;
    team1playerids: string[];
    team2playerids: string[];
    team1score: number;
    team2score: number;
    team1won: boolean;
    matchTime: string;
    hasReplay: boolean;
    roundCount: number;
    roundEnds: { [round: number]: RoundEnd };
    playerTeamId?: string;
    // Private uploads only: which upload this map came from, so its replay rounds can be
    // fetched through the owner-gated /UserDemo endpoint. Absent for league matches.
    uploadId?: string;
}

export interface PlayerSearchResult {
    steamId: string;
    playerName: string;
}

// Builds the season-related query params: prefers a comma-separated `seasons` list
// when multiple are given; falls back to single `season`; omits both for "all seasons".
const seasonParams = (seasons: number[]): Record<string, string | number> => {
    if (seasons.length === 0) return { season: 0 };
    if (seasons.length === 1) return { season: seasons[0] };
    return { season: 0, seasons: seasons.join(',') };
};

export const getMapMatchCounts = async (
    teamId: string,
    seasons: number[]
): Promise<Record<string, number>> => {
    try {
        const response = await axios.get(`${url}/map-counts`, {
            params: { teamId, ...seasonParams(seasons) }
        });
        return response.data;
    } catch {
        return {};
    }
};

// mapName is optional server-side: pass '' for every map the team has played. This is the only
// endpoint that returns a Bo3's individual maps with their gameids — /Match/team runs
// CheckForBO3, which collapses a series into one row with no mapName and no gameid.
export const getMatchesForAnalysis = async (
    teamId: string,
    mapName: string,
    seasons: number[],
    opponentId?: string
): Promise<AnalysisMatch[]> => {
    try {
        const response = await axios.get(`${url}/matches`, {
            params: {
                teamId,
                ...(mapName ? { mapName } : {}),
                ...(opponentId ? { opponentId } : {}),
                ...seasonParams(seasons),
            }
        });
        return response.data;
    } catch {
        return [];
    }
};

export const searchPlayers = async (query: string): Promise<PlayerSearchResult[]> => {
    if (!query || query.length < 2) return [];
    try {
        const response = await axios.get(`${url}/player-search`, { params: { query } });
        return response.data;
    } catch {
        return [];
    }
};

export const getPlayerMapMatchCounts = async (
    steamId: string,
    seasons: number[]
): Promise<Record<string, number>> => {
    try {
        const response = await axios.get(`${url}/player-map-counts`, {
            params: { steamId, ...seasonParams(seasons) }
        });
        return response.data;
    } catch {
        return {};
    }
};

// One cell of the buy/site grid: how many rounds were played in this (buyType, site)
// combination, and how many were won.
export interface BuySiteCell {
    rounds: number;
    wins: number;
}

// Per-perspective grid: outer key = buy type, inner key = site ("A" | "B" | "none").
// "none" covers rounds where no plant happened and T-side commit wasn't inferable.
export type BuySiteGrid = Record<string, Record<string, BuySiteCell>>;

export interface BuySiteWinRate {
    t: BuySiteGrid;
    ct: BuySiteGrid;
}

export const getBuySiteWinRate = async (
    teamId: string,
    mapName: string,
    seasons: number[]
): Promise<BuySiteWinRate> => {
    try {
        const response = await axios.get(`${url}/buysite-winrate`, {
            params: { teamId, mapName, ...seasonParams(seasons) }
        });
        return response.data;
    } catch {
        return { t: {}, ct: {} };
    }
};

export const getMatchesForPlayerAnalysis = async (
    steamId: string,
    mapName: string,
    seasons: number[]
): Promise<AnalysisMatch[]> => {
    try {
        const response = await axios.get(`${url}/player-matches`, {
            params: { steamId, mapName, ...seasonParams(seasons) }
        });
        return response.data;
    } catch {
        return [];
    }
};
